import { post } from "aws-amplify/api";

export interface PersonaSummary {
  title: string;
  role: string;
  market: string;
  focus: string;
  recommendations: string[];
  suggestedQuestions: string[];
}

export interface SummarizePersonaInput {
  personaId: string;
  selectedNeeds: string[];
  regions: string[];
  markets: string[];
  description: string;
}

const MAX_FIELD_LENGTH = 300;

const sanitize = (value: string) =>
  value.replace(/[\r\n]+/g, ' ').slice(0, MAX_FIELD_LENGTH);

export interface ProjectTaskSuggestion {
  title: string;
  description: string;
}

export const generateProjectTasks = async (
  projectTitle: string,
  projectDescription: string
): Promise<ProjectTaskSuggestion[]> => {
  const restOperation = post({
    apiName: "aiAgentApi",
    path: "/api/generate-project-tasks",
    options: {
      body: {
        projectTitle: projectTitle.slice(0, 300),
        projectDescription: projectDescription.slice(0, 1000),
      },
    },
  });
  const response = await restOperation.response;
  const parsed = (await response.body.json() as unknown) as { tasks?: unknown };

  if (!Array.isArray(parsed?.tasks)) {
    throw new Error('Unexpected task response shape from Erode AI agent');
  }

  const tasks = parsed.tasks.filter(
    (task): task is ProjectTaskSuggestion =>
      !!task &&
      typeof (task as ProjectTaskSuggestion).title === 'string' &&
      typeof (task as ProjectTaskSuggestion).description === 'string'
  );

  if (tasks.length === 0) {
    throw new Error('Erode AI agent returned no valid tasks');
  }

  return tasks;
};

export const summarizePersona = async (input: SummarizePersonaInput): Promise<PersonaSummary> => {
  const safeNeeds = (input.selectedNeeds || []).map(sanitize).join(', ');
  const safeRegions = (input.regions || []).map(sanitize).join(', ');
  const safeMarkets = (input.markets || []).map(sanitize).join(', ');
  const safeDescription = sanitize(input.description || 'No description provided');

  const userPrompt = `
Selected needs: ${safeNeeds}
Regions: ${safeRegions}
Markets: ${safeMarkets}
Description: ${safeDescription}
  `.trim();

  // systemPrompt is intentionally not sent — it is defined server-side in the
  // aiAgentFunction Lambda so the client cannot override the persona framing.
  const body = {
    personaId: input.personaId,
    userPrompt,
  };

  const restOperation = post({
    apiName: "aiAgentApi",
    path: "/api/summarize-persona",
    options: { body },
  });
  const response = await restOperation.response;
  const parsed = (await response.body.json() as unknown) as { summary: PersonaSummary };

  return parsed.summary;
};
